/**
 * ApplyLeave.jsx — Iter 123. Single page where any employee can:
 *   • see their leave balance
 *   • apply for a new leave
 *   • see history + approval status
 *
 * Lives at `/hr/apply-leave` (sidebar Overview group, key='dashboard'
 * so it's accessible to every employee, not just HR).
 *
 * Mobile mirror at `/m/leave`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';
import { Calendar, Plus, X, FileText, Loader2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';

const LEAVE_TYPES = [
  { code: 'CL', label: 'Casual Leave',  paid: true },
  { code: 'SL', label: 'Sick Leave',    paid: true },
  { code: 'EL', label: 'Earned Leave',  paid: true },
  { code: 'PL', label: 'Privilege Leave', paid: true },
  { code: 'Comp-off', label: 'Comp-off', paid: true },
  { code: 'LWP', label: 'Unpaid Leave (LWP)', paid: false },
];

const STATUS_CLR = {
  Pending:  'bg-amber-50 text-amber-700 border-amber-200',
  Approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Rejected: 'bg-rose-50 text-rose-700 border-rose-200',
};

export default function ApplyLeave({ compact = false }) {
  const [balance, setBalance] = useState([]);
  const [history, setHistory] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    leave_type: 'Casual Leave',
    from_date: new Date().toISOString().slice(0, 10),
    to_date: new Date().toISOString().slice(0, 10),
    reason: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const me = await apiGet('/hr/me');
      setProfile(me);
      if (me?.linked) {
        const emp = me.employee || {};
        const [bal, leaves] = await Promise.all([
          apiGet('/hr/me/leave-balance').catch(() => ({ balance: [] })),
          apiGet('/hr/leaves').catch(() => []),
        ]);
        setBalance(bal.balance || []);
        // server returns ALL leaves; filter to current employee
        const all = Array.isArray(leaves) ? leaves : (leaves.leaves || leaves.items || []);
        setHistory(all.filter(l => l.employee_id === emp.id || l.emp_code === emp.emp_id));
      }
    } catch { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const daysCount = useMemo(() => {
    if (!form.from_date || !form.to_date) return 0;
    const a = new Date(form.from_date);
    const b = new Date(form.to_date);
    if (b < a) return 0;
    return Math.round((b - a) / 86400000) + 1;
  }, [form.from_date, form.to_date]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!profile?.linked) { toast.error('Your account is not linked to an employee record yet'); return; }
    const emp = profile.employee || {};
    if (!form.reason.trim()) { toast.error('Please provide a reason for the leave'); return; }
    if (daysCount <= 0) { toast.error('Invalid date range'); return; }
    setSubmitting(true);
    try {
      await apiPost('/hr/leaves', {
        employee_id: emp.id,
        emp_code: emp.emp_id,
        employee_name: emp.name,
        leave_type: form.leave_type,
        from_date: form.from_date,
        to_date: form.to_date,
        days: daysCount,
        reason: form.reason,
        status: 'Pending',
      });
      toast.success(`Leave request submitted (${daysCount} day${daysCount > 1 ? 's' : ''})`);
      setShowForm(false);
      setForm({ leave_type: 'Casual Leave', from_date: new Date().toISOString().slice(0, 10), to_date: new Date().toISOString().slice(0, 10), reason: '' });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit leave');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>;
  if (!profile?.linked) return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="bg-amber-50 border border-amber-200 rounded-md p-5 text-sm text-amber-800">
        <b>Your account isn't linked to an employee record yet.</b><br />
        Please ask HR to link your account to apply for leaves.
      </div>
    </div>
  );

  return (
    <div className={compact ? "p-3" : "p-4 md:p-8 max-w-5xl mx-auto"} data-testid="apply-leave-page">
      {!compact && (
        <PageHeader
          overline="Self Service · Employee"
          title="Apply for Leave"
          subtitle={`Hi ${(profile.employee?.name || '').split(' ')[0]} — request a leave, track approval status, and check your remaining balance.`}
          actions={
            <button onClick={() => setShowForm(true)} data-testid="apply-leave-new-btn" className="flex items-center gap-2 px-4 py-2.5 bg-[#1F7A6E] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#14504E]">
              <Plus size={15} /> New Leave Request
            </button>
          }
        />
      )}
      {compact && (
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-base">Apply for Leave</h2>
          <button onClick={() => setShowForm(true)} data-testid="apply-leave-new-btn" className="px-3 py-1.5 bg-[#1F7A6E] text-white text-xs font-display font-semibold rounded-sm flex items-center gap-1.5">
            <Plus size={12} /> New
          </button>
        </div>
      )}

      {/* Balance */}
      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Calendar size={14} className="text-[#1F7A6E]" />
          <div className="kpi-overline">Leave Balance · {new Date().getFullYear()}</div>
        </div>
        {balance.length === 0 ? (
          <div className="text-xs text-slate-400 italic">No leave policy assigned yet.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {balance.map((b) => (
              <div key={b.leave_type} className="border border-slate-200 rounded p-3 bg-slate-50/40">
                <div className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">{b.leave_type}</div>
                <div className="font-display font-bold text-xl mt-1 tabular-nums">{b.remaining}</div>
                <div className="text-[10px] text-slate-500">of {b.entitlement} · {b.used} used</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* History */}
      <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="apply-leave-history">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <FileText size={14} className="text-[#1F7A6E]" />
            <div className="kpi-overline">Your Leave Requests · {history.length}</div>
          </div>
        </div>
        {history.length === 0 ? (
          <div className="text-xs text-slate-400 italic py-6 text-center">No leave requests yet. Click "New Leave Request" to apply.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Type</th>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">From</th>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">To</th>
                  <th className="text-right py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Days</th>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Reason</th>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Status</th>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Approver</th>
                </tr>
              </thead>
              <tbody>
                {history.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).map((l, i) => (
                  <tr key={l.id || i} className={i % 2 ? 'bg-slate-50/40' : ''}>
                    <td className="py-2 px-2 font-display font-semibold">{l.leave_type}</td>
                    <td className="py-2 px-2 tabular-nums">{l.from_date}</td>
                    <td className="py-2 px-2 tabular-nums">{l.to_date}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{l.days}</td>
                    <td className="py-2 px-2 max-w-[200px] truncate" title={l.reason}>{l.reason}</td>
                    <td className="py-2 px-2"><span className={`tag border ${STATUS_CLR[l.status] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>{l.status || 'Pending'}</span></td>
                    <td className="py-2 px-2 text-xs text-slate-500">{l.approver || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal — apply form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center md:p-4">
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-lg md:rounded-md flex flex-col max-h-[92vh]">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">New Leave Request</h3>
              <button onClick={() => setShowForm(false)} data-testid="apply-leave-close-btn" className="w-9 h-9 flex items-center justify-center hover:bg-slate-100 rounded"><X size={16} /></button>
            </div>
            <form onSubmit={submit} className="p-4 space-y-4 overflow-y-auto">
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Leave Type</label>
                <select value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })} data-testid="apply-leave-type" className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm bg-white">
                  {LEAVE_TYPES.map(t => <option key={t.code} value={t.label}>{t.label} {!t.paid && '(Unpaid)'}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">From</label>
                  <input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} data-testid="apply-leave-from" className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm" required />
                </div>
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">To</label>
                  <input type="date" value={form.to_date} min={form.from_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} data-testid="apply-leave-to" className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm" required />
                </div>
              </div>
              <div className="bg-teal-50/40 border border-teal-200 rounded p-2 text-xs text-slate-700">
                <b>{daysCount}</b> day{daysCount !== 1 ? 's' : ''} requested.
                {daysCount > 0 && form.leave_type && LEAVE_TYPES.find(t => t.label === form.leave_type)?.paid && <span className="text-emerald-700 ml-2">· Will be auto-marked as Paid Leave in attendance on approval.</span>}
                {daysCount > 0 && form.leave_type && !LEAVE_TYPES.find(t => t.label === form.leave_type)?.paid && <span className="text-rose-700 ml-2">· Will be counted as LOP (Loss of Pay).</span>}
              </div>
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Reason</label>
                <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="apply-leave-reason" rows={3} placeholder="Brief reason for the leave" className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm" required />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-slate-300 rounded font-display">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="apply-leave-submit-btn" className="px-5 py-2 text-sm bg-[#1F7A6E] text-white rounded font-display font-semibold disabled:opacity-50 flex items-center gap-2">
                  {submitting && <Loader2 size={12} className="animate-spin" />}
                  {submitting ? 'Submitting…' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
